// Step6Confirm — ActivityWizard Steg 6 (delspår 3g): read-only sammanfattning av allt valt.
// "Skapa aktivitet"-knappen är footer-action:en (ActivityWizard.handleNext → submitActivity
// på sista steget). Den här vyn skapar inget själv — bara visar vad som kommer skapas.

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Clock, Globe, LayoutGrid, Lock, Signal, Target, Timer, Trophy, Users, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useActivityWizard } from "../ActivityWizardContext";
import { useActivityTypes } from "@/hooks/useActivityTypes";
import { useOrgHall } from "@/hooks/useOrgHall";
import { deriveRequiredCourts } from "@/lib/wizardSlots";
import WizardStepHero from "../WizardStepHero";

type CourtRow = { id: string; name: string };

export default function Step6Confirm() {
  const { t } = useTranslation("admin");
  const { state } = useActivityWizard();
  const { types } = useActivityTypes();
  const { courts } = useOrgHall();
  const fd = state.formData;

  const selectedType = types.find((tp) => tp.id === fd.activityTypeId) ?? null;
  const isTournament = !!selectedType?.tournament_format;
  const wantsCourt = fd.wantsCourt ?? true;
  const courtMode = fd.courtMode ?? "auto";
  const spotsTotal = fd.spotsTotal ?? 4;
  const n = deriveRequiredCourts(spotsTotal);

  const courtSummary = useMemo(() => {
    if (!wantsCourt) return t("activityWizard.step6.courtNone", { defaultValue: "Ingen bana" });
    if (courtMode === "auto")
      return t("activityWizard.step6.courtAuto", { n, defaultValue: `Vilken som helst — ${n} banor` });
    const names = (fd.courtIds ?? [])
      .map((id) => (courts as CourtRow[]).find((c) => c.id === id)?.name)
      .filter(Boolean)
      .join(", ");
    return t("activityWizard.step6.courtSpecific", { courts: names, defaultValue: names || "—" });
  }, [wantsCourt, courtMode, n, fd.courtIds, courts, t]);

  // icon is decorative only (an accent-soft plate per row); it never changes the rendered value text.
  const rows: Array<{ label: string; value: string; icon: LucideIcon }> = [
    { label: t("activityWizard.step6.type", { defaultValue: "Aktivitet" }), value: selectedType?.name ?? "—", icon: Trophy },
    { label: t("activityWizard.step6.date", { defaultValue: "Datum" }), value: fd.date ?? "—", icon: Calendar },
    {
      label: t("activityWizard.step6.duration", { defaultValue: "Längd" }),
      value: fd.durationMinutes ? `${fd.durationMinutes} min` : "—",
      icon: Timer,
    },
    {
      label: t("activityWizard.step6.time", { defaultValue: "Tid" }),
      value: fd.startTime ? `${fd.startTime}${fd.endTime ? `–${fd.endTime}` : ""}` : "—",
      icon: Clock,
    },
    { label: t("activityWizard.step6.court", { defaultValue: "Bana" }), value: courtSummary, icon: LayoutGrid },
    { label: t("activityWizard.step6.players", { defaultValue: "Antal spelare" }), value: String(spotsTotal), icon: Users },
    {
      label: t("activityWizard.step6.level", { defaultValue: "Nivå" }),
      value: `${fd.minLevel ?? 1}–${fd.maxLevel ?? 10}`,
      icon: Signal,
    },
    ...(isTournament
      ? [{ label: t("activityWizard.step6.points", { defaultValue: "Antal poäng" }), value: String(fd.targetPoints ?? 32), icon: Target }]
      : []),
    {
      label: t("activityWizard.step6.visibility", { defaultValue: "Synlighet" }),
      value:
        fd.visibility === "private"
          ? t("activityWizard.step6.visibilityPrivate", { defaultValue: "Privat" })
          : t("activityWizard.step6.visibilityPublic", { defaultValue: "Öppen" }),
      icon: fd.visibility === "private" ? Lock : Globe,
    },
  ];

  return (
    <section className="space-y-4" data-testid="step6-confirm">
      <WizardStepHero
        title={t("activityWizard.step6.heroTitle", { defaultValue: "Ser det rätt ut?" })}
        subtitle={t("activityWizard.step6.heroSubtitle", {
          defaultValue: "Granska innan du skapar. Du kan ändra namnet nedan.",
        })}
      />
      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <div
              key={r.label}
              className="flex items-center gap-3 border-b border-border/60 px-4 py-3 last:border-b-0"
            >
              <span className={cn("grid h-9 w-9 flex-none place-items-center rounded-xl bg-primary/10 text-primary")}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="text-sm text-muted-foreground">{r.label}</span>
              <span className="ml-auto text-right text-sm font-medium tabular-nums">{r.value}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
